<i18n lang="yaml">
pt:
  cardOptions: 'Opções do card'
  publicPrivacyNotice: 'Aberto: Os dados dessa página são associados ao seu usuário e podem ser acessados pelos administradores do sistema.'
  confidentialPrivacyNotice: 'Confidencial: O sistema registra seus dados, mas nenhum administrador têm acesso nominal às suas respostas.'
  anonymousPrivacyNotice: 'Anônimo: Suas respostas são registradas de modo criptografado e sem nenhuma informação pessoal associada.'
en:
  cardOptions: 'Card options'
  publicPrivacyNotice: 'Public: The information in this page is associated to you and can be accessed by the system administrators.'
  confidentialPrivacyNotice: 'Confidential: The system will register your information, but it will not be available to any other user.'
  anonymousPrivacyNotice: 'Anonymous: Your information is encrypted before it is registered, with no personal information associated.'
</i18n>

<template>
  <deck-row
    class="deck-exodia-header align-baseline"
    no-gutters
  >
    <deck-col
      v-if="avatarIcon || avatarImageUrl"
      class="flex-grow-0 align-self-center"
      auto
    >
      <div
        v-test-id="'deck-exodia-header-avatar-image'"
        :class="{
          'deck-exodia-header__avatar': true,
          [`deck-exodia-header__avatar--${size}`]: true,
          'deck-exodia-header__avatar--contain': avatarImageContain,
        }"
        :style="`background-image: url(${avatarImageUrl});`"
      >
        <v-icon
          v-if="avatarIcon"
          class="text--primary"
        >
          fa-regular {{ avatarIcon }}
        </v-icon>
      </div>
    </deck-col>

    <deck-col
      class="flex-1 min-w-0 align-self-center"
      auto
    >
      <deck-trimmer
        v-if="title"
        :text="title"
        :class="{ [`deck-exodia-header__title--${size}`]: true }"
        :line-clamp="titleLineClamp"
        class="deck-exodia-header__title mb-0"
        tag="p"
        role="heading"
        aria-level="2"
      />
      <deck-trimmer
        v-if="subtitle"
        :class="`deck-exodia-header__subtitle--${size}`"
        :text="subtitle"
        :line-clamp="subtitleLineClamp"
        class="deck-exodia-header__subtitle mb-0"
        tag="p"
        role="heading"
        aria-level="3"
      />
    </deck-col>

    <slot name="top-content" />

    <deck-col
      v-if="Boolean(privacy)"
      class="flex-grow-0"
      auto
    >
      <deck-chip
        :icon="privacyIcon"
        :text="t(`${privacy}PrivacyNotice`)"
      />
    </deck-col>

    <deck-col
      v-if="$slots['menu-items']"
      class="flex-grow-0 deck-exodia-header__menu"
      auto
    >
      <v-menu
        min-width="200"
        location="bottom"
      >
        <template #activator="{ props }">
          <deck-button
            :size="size"
            is-ready
            icon="fa-ellipsis-v"
            icon-kind="solid"
            kind="ghost"
            color="controls"
            :aria-label="t('cardOptions')"
            v-bind="props"
            @click.prevent
          />
        </template>
        <slot name="menu-items" />
      </v-menu>
    </deck-col>
  </deck-row>
</template>

<script>
import DeckChip from '~/deck/chip';
import DeckTrimmer from '~/deck/trimmer';

export default {
  name: 'DeckExodiaHeader',
  components: {
    DeckChip,
    DeckTrimmer,
  },
  props: {
    title: { type: String, default: null },
    subtitle: { type: String, default: null },
    avatarIcon: { type: String, default: null },
    avatarImageUrl: { type: String, default: null },
    avatarImageContain: { type: Boolean, default: false },
    size: { type: String, required: true },
    titleLineClamp: { type: [Number, String], default: 1 },
    subtitleLineClamp: { type: [Number, String], default: 2 },
    privacy: { type: String, default: null },
  },
  setup() {
    return {
      t: useI18n().t,
    };
  },
  computed: {
    privacyIcon() {
      switch (this.privacy) {
        case 'public':
          return 'fa-regular fa-user-unlock';
        case 'confidential':
          return 'fa-regular fa-user-lock';
        case 'anonymous':
          return 'fa-regular fa-user-shield';
        default:
          return null;
      }
    },
  },
};
</script>

<style lang="scss">
.deck-exodia-header {
  flex-grow: 0;
  flex-wrap: nowrap;
}

.deck-exodia-header__title {
  position: relative;
  font-size: 24px !important;
  line-height: 30px;
  font-weight: 500;
}

.deck-exodia-header__title--small{
  font-size: 16px !important;
  line-height: 20px;
}

.deck-exodia-header__subtitle {
  font-size: 16px !important;
  line-height: 20px;
  color: var(--z-color-label) !important;
  margin-top: var(--z-s1);
}

.deck-exodia-header__subtitle--small{
  font-size: 12px !important;
  line-height: 16px;
}

.deck-exodia-header__avatar {
  height: 56px;
  width: 56px;
  margin-right: var(--z-s2) !important;
  border-radius: var(--z-border-radius-inner-base);
  background-size: cover;

  .v-icon {
    font-size: 36px;
    height: 100%;
    width: 100%;
  }
}

.deck-exodia-header__avatar--small {
  height: 40px;
  width: 40px;

  .v-icon {
    font-size: 28px;
    height: 100%;
    width: 100%;
  }
}

.deck-exodia-header__avatar--contain {
  background-size: contain;
}

.deck-exodia--small:not(:hover) .deck-exodia-header__menu {
  visibility: hidden;
}
</style>
